import type { EndorsementResponse } from "@tm/insurance/api";
import { submitEndorsement } from "@tm/insurance/api";
import {
  FormattedText,
  ImageComponent,
  LandingHeaderMobile,
  ReactPortalComp,
  Spinner,
} from "@tm/static";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";
import React, { useEffect, useRef, useState } from "react";
import CommonModalDweb from "../../../../components/CommonModalDweb/CommonModalDweb";
import { ORDER_DETAIL_V2_ASSETS } from "../../constants";
import type {
  AddAlternateContactModalProps,
  AlternateContactValues,
  ContactFormFieldConfig,
  EndorsementView,
} from "../../types";
import {
  EditTravellersSheetStyle,
  SheetContent,
  SheetFooter,
  StickyErrorBanner,
  StickyErrorIcon,
  StickyErrorText,
} from "../EditTravellersModal/EditTravellersModal.style";
import FormInput from "../FormInput";
import { InfoModalContent } from "../InfoModalContent";
import { TextButton } from "../TextButton";
import {
  ContactFormFields,
  ContactFormSection,
  FooterButtonRow,
  FooterLeft,
  FooterRight,
  ModalFooter,
  ModalSubtitle,
  SaveButton,
} from "./AddAlternateContactModal.style";

const FIELD_ORDER: Record<string, number> = {
  name: 1,
  phone: 2,
  email: 3,
};

const validateField = (
  value: string,
  field: ContactFormFieldConfig
): string => {
  if (!field.validation) return "";

  if (field.isRequired && (!value || value.trim() === "")) {
    return field.validation.errorMessage || "This field is required";
  }

  if (value && field.validation.pattern) {
    const pattern = field.validation.pattern.replace(/\\\\/g, "\\");
    const regex = new RegExp(pattern);
    if (!regex.test(value)) {
      return field.validation.errorMessage || "Invalid value";
    }
  }

  return "";
};

export const AddAlternateContactModal: React.FC<
  AddAlternateContactModalProps
> = ({
  isOpen,
  onClose,
  formConfig,
  modalConfig,
  initialValues,
  bookingId,
  primaryEntity,
  onSave,
  onEndorsementSuccess,
}) => {
  const device = useBreakpointV2();
  const [formData, setFormData] = useState<AlternateContactValues>({
    name: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [endorsementView, setEndorsementView] =
    useState<EndorsementView>("form");
  const [endorsementData, setEndorsementData] =
    useState<EndorsementResponse | null>(null);
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const initialDataRef = useRef<AlternateContactValues>({});
  const prevIsOpenRef = useRef(false);
  const pendingRefreshRef = useRef(false);

  const header = modalConfig?.header || {};
  const footerCtas = modalConfig?.footer?.cta || [];
  const backCta = footerCtas.find((c) => c?.actionId === "CLOSE_FORM");
  const saveCta = footerCtas.find((c) => c?.actionId === "SAVE_CONTACT");

  const fields = formConfig?.fields
    ? Object.entries(formConfig.fields)
        .filter(([, f]) => f?.isVisible !== false)
        .sort(([a], [b]) => (FIELD_ORDER[a] ?? 99) - (FIELD_ORDER[b] ?? 99))
    : [];

  useEffect(() => {
    if (isOpen) {
      const isModalJustOpened = !prevIsOpenRef.current;
      prevIsOpenRef.current = true;
      const initial: AlternateContactValues = {
        name: initialValues?.name ?? "",
        email: initialValues?.email ?? "",
        phone: initialValues?.phone ?? "",
      };
      setFormData(initial);
      setErrors({});
      initialDataRef.current = { ...initial };
      setIsLoading(false);
      if (isModalJustOpened) {
        setEndorsementView("form");
        setEndorsementData(null);
        setApiErrorMessage(null);
      }
    } else {
      prevIsOpenRef.current = false;
    }
  }, [isOpen, initialValues?.name, initialValues?.email, initialValues?.phone]);

  // Returns the initial (masked) value for a field from the snapshot taken when the modal opened
  const getInitialValue = (fieldKey: string): string =>
    String(
      initialDataRef.current[fieldKey as keyof AlternateContactValues] ?? ""
    );

  const handleMaskedFocus = (fieldKey: string) => {
    // Clear the masked value so the user can type a fresh value
    setFormData((prev) => ({ ...prev, [fieldKey]: "" }));
  };

  const handleMaskedBlur = (
    fieldKey: string,
    currentValue: string,
    field: ContactFormFieldConfig
  ) => {
    // If the user didn't type anything, restore the original masked value
    if (!currentValue.trim()) {
      const maskedValue = getInitialValue(fieldKey);
      setFormData((prev) => ({ ...prev, [fieldKey]: maskedValue }));
      setErrors((prev) => ({ ...prev, [fieldKey]: "" }));
      return;
    }
    // User typed something new — run normal validation
    const error = validateField(currentValue, field);
    setErrors((prev) => ({ ...prev, [fieldKey]: error }));
  };

  const handleFieldChange = (fieldKey: string, value: string) => {
    if (apiErrorMessage) setApiErrorMessage(null);
    setFormData((prev) => ({ ...prev, [fieldKey]: value }));
    const field = formConfig?.fields?.[fieldKey];
    if (field) {
      const error = validateField(value, field);
      setErrors((prev) => ({ ...prev, [fieldKey]: error }));
    }
  };

  const hasFormDataChanged = (): boolean => {
    const initial = initialDataRef.current;
    return (
      (formData.name ?? "") !== (initial.name ?? "") ||
      (formData.email ?? "") !== (initial.email ?? "") ||
      (formData.phone ?? "") !== (initial.phone ?? "")
    );
  };

  const validateAllFields = (): boolean => {
    if (!formConfig?.fields) return true;
    const newErrors: Record<string, string> = {};
    let isValid = true;

    fields.forEach(([fieldKey, field]) => {
      if (field.isVisible && field.isEnabled) {
        const value = formData[fieldKey as keyof AlternateContactValues] ?? "";

        // Skip validation for masked fields that haven't been changed
        if (field.isMasked && value === getInitialValue(fieldKey)) return;

        const error = validateField(value, field);
        if (error) {
          isValid = false;
          newErrors[fieldKey] = error;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validateAllFields()) return;

    setIsLoading(true);
    try {
      if (!primaryEntity) {
        throw new Error("Primary entity is required");
      }
      const payload = [
        {
          entityId: primaryEntity.id,
          ...(primaryEntity.data as Record<string, unknown>),
          alternateContactNo: formData.phone ?? "",
          alternateEmail: formData.email ?? "",
        },
      ];
      const res = await submitEndorsement({ bookingId, payload });
      setEndorsementData(res);
      const status = (res?.status ?? "").toUpperCase();
      if (status === "SUCCESS") {
        setEndorsementView("success");
        setApiErrorMessage(null);
        initialDataRef.current = { ...formData };
        await Promise.resolve(
          onSave({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
          })
        );
        pendingRefreshRef.current = true;
      } else if (status === "ERROR" || status === "VALIDATION_ERROR") {
        setApiErrorMessage(
          res?.desc ||
            res?.subtitle ||
            res?.title ||
            "An error occurred. Please try again."
        );
        setEndorsementView("form");
      } else {
        setEndorsementView("generating");
      }
    } catch {
      setApiErrorMessage("An error occurred. Please try again.");
      setEndorsementView("form");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (pendingRefreshRef.current) {
      pendingRefreshRef.current = false;
      onEndorsementSuccess?.();
    }
    onClose();
  };

  if (!isOpen) return null;

  const modalTitle = header?.title ?? "Add Alternate Contact Details";
  const modalSubTitle = header?.subTitle;
  const backLabel = backCta?.label ?? "GO BACK";
  const saveLabel = saveCta?.label ?? "Save Details";
  const hasChanges = hasFormDataChanged();

  const renderResultView = () => (
    <ContactFormSection>
      <InfoModalContent
        viewType={endorsementView}
        data={endorsementData}
        renderAction={(data) => {
          if (
            endorsementView === "success" &&
            data.cta?.isEnabled !== false &&
            data.cta?.label &&
            data.newPolicyUrl
          ) {
            return (
              <SaveButton
                as="a"
                href={data.newPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
                hasChanges={true}
                isLoading={false}
                style={{ marginTop: "16px", textDecoration: "none" }}
              >
                <FormattedText value={{ text: { value: data.cta.label } }} />
              </SaveButton>
            );
          }
          return null;
        }}
      />
    </ContactFormSection>
  );

  const isResultView =
    endorsementView === "success" || endorsementView === "generating";

  const isFormValid = (): boolean => {
    if (!formConfig?.fields || fields.length === 0) return false;
    for (const [fieldKey, field] of fields) {
      if (field.isVisible && field.isEnabled) {
        const value = formData[fieldKey as keyof AlternateContactValues] ?? "";
        // Skip validation for masked fields that haven't been changed
        if (field.isMasked && value === getInitialValue(fieldKey)) continue;
        if (validateField(value, field)) return false;
      }
    }
    return true;
  };

  const canSave = hasChanges && isFormValid();

  const renderFormContent = () => (
    <>
      <ContactFormSection>
        {modalSubTitle ? (
          <ModalSubtitle>
            <FormattedText value={{ text: { value: modalSubTitle } }} />
          </ModalSubtitle>
        ) : null}

        <ContactFormFields>
          {fields.map(([fieldKey, field]) => {
            const value =
              formData[fieldKey as keyof AlternateContactValues] ?? "";
            const error = errors[fieldKey];
            const inputType =
              field.type === "EMAIL"
                ? "email"
                : field.type === "TEL"
                ? "tel"
                : "text";

            return (
              <FormInput
                key={fieldKey}
                flex="1"
                labelTxt={field.label}
                placeholderTxt={field.placeholder}
                requiredField={field.isRequired}
                inputValue={value}
                errorState={error}
                hasError={!!error}
                showError={!!error}
                isDisabled={!field.isEnabled}
                inputType={inputType}
                handleChange={(e) =>
                  handleFieldChange(fieldKey, e.target.value)
                }
                handleFocus={
                  field.isMasked ? () => handleMaskedFocus(fieldKey) : undefined
                }
                handleBlur={(e) => {
                  if (field.isMasked) {
                    handleMaskedBlur(fieldKey, e.target.value, field);
                  } else {
                    const err = validateField(e.target.value, field);
                    setErrors((prev) => ({ ...prev, [fieldKey]: err }));
                  }
                }}
                margin="0"
              />
            );
          })}
        </ContactFormFields>
      </ContactFormSection>

      {device === "lg" ? (
        <ModalFooter>
          {apiErrorMessage && (
            <StickyErrorBanner>
              <StickyErrorIcon>
                <ImageComponent
                  src={ORDER_DETAIL_V2_ASSETS.VALIDATION_ERROR_ICON}
                  width="24"
                  height="24"
                  alt="Error"
                />
              </StickyErrorIcon>
              <StickyErrorText>
                <FormattedText value={{ html: apiErrorMessage }} />
              </StickyErrorText>
            </StickyErrorBanner>
          )}
          <FooterButtonRow>
            <FooterLeft>
              {backCta?.isVisible !== false && (
                <TextButton
                  btnTxt={backLabel}
                  showDefaultRightIcon={false}
                  onClick={handleClose}
                  disabled={isLoading}
                />
              )}
            </FooterLeft>
            <FooterRight>
              {saveCta?.isVisible !== false && (
                <SaveButton
                  type="button"
                  onClick={handleSave}
                  hasChanges={canSave}
                  isLoading={isLoading}
                  disabled={!canSave || isLoading}
                >
                  {isLoading ? (
                    <>
                      <Spinner
                        spinnerWidth={16}
                        spinnerColor={theme.whiteText}
                        thickness={2}
                      />
                      <FormattedText value={{ text: { value: saveLabel } }} />
                    </>
                  ) : (
                    <FormattedText value={{ text: { value: saveLabel } }} />
                  )}
                </SaveButton>
              )}
            </FooterRight>
          </FooterButtonRow>
        </ModalFooter>
      ) : (
        <>
          {apiErrorMessage && (
            <StickyErrorBanner>
              <StickyErrorIcon>
                <ImageComponent
                  src={ORDER_DETAIL_V2_ASSETS.VALIDATION_ERROR_ICON}
                  width="24"
                  height="24"
                  alt="Error"
                />
              </StickyErrorIcon>
              <StickyErrorText>
                <FormattedText value={{ html: apiErrorMessage }} />
              </StickyErrorText>
            </StickyErrorBanner>
          )}
          <SheetFooter>
            {saveCta?.isVisible !== false && (
              <SaveButton
                type="button"
                onClick={handleSave}
                hasChanges={canSave}
                isLoading={isLoading}
                disabled={!canSave || isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner
                      spinnerWidth={16}
                      spinnerColor="#ffffff"
                      thickness={2}
                    />
                    <FormattedText value={{ text: { value: saveLabel } }} />
                  </>
                ) : (
                  <FormattedText value={{ text: { value: saveLabel } }} />
                )}
              </SaveButton>
            )}
          </SheetFooter>
        </>
      )}
    </>
  );

  // Mobile: Render as full-page sheet
  if (device === "sm") {
    return (
      <ReactPortalComp>
        <EditTravellersSheetStyle>
          <LandingHeaderMobile
            displayWhiteHeader
            headerTitle={isResultView ? "" : modalTitle}
            handleBackBtn={handleClose}
          />
          <SheetContent>
            {isResultView ? renderResultView() : renderFormContent()}
          </SheetContent>
        </EditTravellersSheetStyle>
      </ReactPortalComp>
    );
  }

  // Desktop: Render as modal
  return (
    <CommonModalDweb
      customTitle={isResultView ? "" : modalTitle}
      handleClick={handleClose}
      customWidth={isResultView ? "420px" : "480px"}
      nonClipHeading
    >
      {isResultView ? renderResultView() : renderFormContent()}
    </CommonModalDweb>
  );
};
